// components/OttoCalibration.tsx
// Otto 보정 패널 하나로 합침: 1 조립 자세 → 2 다리 수직(90도) → 3 굴리기 시험(두 발 속도) → 4 걷기 시험(발을 들 때 기울기) → 5 댄스 시험(한 발로 설 때 엉덩이 각도).
// 값을 바꾸면 MQTT(oc 명령)로 보드에 바로 반영되고, [저장]을 누르면 보드의 NVS 에 기록된다.
// 보드는 부팅할 때 저장된 값을 읽어 쓴다. (OttoI2R 라이브러리의 ottoLoadCalibration)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MQTTService from './MQTTService';

interface Props {
  mac: string;
  email: string;
  mqttMessage?: string; // App 이 내려주는 최근 MQTT 메시지(JSON 문자열)
  onClose: () => void;
}

interface CalValues {
  yl: number; // 왼쪽 다리가 수직일 때의 펄스폭(us)
  yr: number; // 오른쪽 다리
  tr: number; // 걷기: 오른발을 들 때 기울기(도)
  tl: number; // 걷기: 왼발을 들 때
  rl: number; // 왼발 굴림 속도(us)
  rr: number; // 오른발 굴림 속도(us)
  dRYr: number; // 댄스: 왼발로 설 때 오른쪽 엉덩이(도)
  dRYl: number; //        왼발로 설 때 왼쪽 엉덩이
  dLYr: number; //        오른발로 설 때 오른쪽 엉덩이
  dLYl: number; //        오른발로 설 때 왼쪽 엉덩이
}

// 펌웨어와 라이브러리가 쓰는 범위와 같다
const RANGE = {
  center: [1000, 1900],
  tilt: [0, 45],
  speed: [0, 600],
  // 엉덩이 안전 범위. 벗어나면 서보가 망가질 수 있어 보드(라이브러리)도 같은 범위로 맞춘다
  hipYr: [0, 135],
  hipYl: [55, 180],
  // 댄스 슬라이더 범위: 왼발 서기는 120 중심 90~150, 오른발 서기는 60 중심 30~90 이되, 위의 안전 범위와 겹치는 부분만 쓴다
  //  왼발 서기 YR 90~135 (150 은 안전 범위 밖), YL 90~150 / 오른발 서기 YR 30~90, YL 55~90 (30 은 안전 범위 밖)
  danceRYr: [90, 135],
  danceRYl: [90, 150],
  danceLYr: [30, 90],
  danceLYl: [55, 90],
} as const;

const DEFAULTS: CalValues = { yl: 1250, yr: 1450, tr: 20, tl: 23, rl: 450, rr: 550, dRYr: 129, dRYl: 129, dLYr: 51, dLYl: 55 };

const panelStyle: React.CSSProperties = {
  marginBottom: '20px', padding: '20px 22px', border: '1px solid #e2e8f0', borderRadius: '20px', background: '#ffffff',
  boxShadow: '0 8px 24px -8px rgba(15, 23, 42, 0.06)',
};
const stepStyle: React.CSSProperties = { fontWeight: 700, color: '#0f172a', margin: '18px 0 6px', fontSize: '0.95rem' };
const hintStyle: React.CSSProperties = { fontSize: '0.84rem', color: '#64748b', marginBottom: '10px', lineHeight: 1.5 };
const smallBtn: React.CSSProperties = { padding: '5px 11px', border: '1px solid #c7d2fe', borderRadius: '8px', background: '#eef2ff', color: '#3730a3', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' };
// 버튼 색: 자세 이동(청록) / 시작(초록) / 정지(주황) / 복사(회색)
const colorBtn = (bg: string, fg = '#ffffff'): React.CSSProperties => ({ backgroundColor: bg, color: fg, border: 'none', fontWeight: 600, borderRadius: '10px' });
const moveBtnStyle = colorBtn('#0891b2');
const startBtnStyle = colorBtn('#16a34a');
const startBtnOnStyle = colorBtn('#166534');
const stopBtnStyle = colorBtn('#f59e0b', '#1f2937');
const grayBtnStyle = colorBtn('#64748b');
const closeBtnStyle: React.CSSProperties = { ...smallBtn, border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#334155' };

const OttoCalibration: React.FC<Props> = ({ mac, email, mqttMessage, onClose }) => {
  const [v, setV] = useState<CalValues>(DEFAULTS);
  const [saved, setSaved] = useState<boolean | null>(null); // 보드 NVS 에 저장된 값이 있는지 (null = 아직 모름)
  const [loaded, setLoaded] = useState(false);
  const [testing, setTesting] = useState<string | null>(null); // 시험 중인 동작 (rf 굴리기 / wf 걷기 / dn 댄스)
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const [waiting, setWaiting] = useState('');   // 보드 응답을 기다리는 중인 동작 이름
  const [pressed, setPressed] = useState('');   // 방금 누른 버튼 (눌렸다는 표시로 잠깐 떨린다)
  const waitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const touchedAt = useRef(0);
  const sentAt = useRef(0);
  const pending = useRef<Partial<Record<keyof CalValues, number>>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const testingRef = useRef(false);

  const pub = useCallback((action: string, extra: Record<string, any> = {}) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'ottoCal', mac, email, action, ...extra })
    );
  }, [mac, email]);

  // 버튼을 눌렀다는 표시: 버튼이 잠깐 떨리고, 보드가 3초 안에 응답하지 않으면 안내한다
  const flash = useCallback((key: string) => {
    setPressed(key);
    setTimeout(() => setPressed(''), 450);
  }, []);

  const send = useCallback((key: string, label: string, action: string) => {
    flash(key);
    setError('');
    setMsg('');
    setWaiting(label);
    pub(action);
    if (waitTimer.current) clearTimeout(waitTimer.current);
    waitTimer.current = setTimeout(() => {
      setWaiting('');
      setError('보드가 3초 안에 응답하지 않았습니다. 보드가 켜져 있고 인터넷에 연결됐는지, 보정 명령(oc)이 들어간 새 펌웨어가 올려져 있는지 확인하세요.');
    }, 3000);
  }, [pub, flash]);

  const pubMove = useCallback((action: string) => {
    if (!mac || !email) return;
    const message: Record<string, any> = { command: 'ottoAction', mac, email, action };
    if (action !== 'stop') message.duration = 0; // 정지 명령이 올 때까지 계속
    MQTTService.getInstance().publish(`i2r/${email}/in`, JSON.stringify(message));
  }, [mac, email]);

  // 처음 열면 보드의 현재 값을 읽는다
  useEffect(() => {
    pub('get');
    // 패널을 닫거나 다른 화면으로 가면 시험 동작을 멈춘다
    return () => {
      if (timer.current) clearTimeout(timer.current);
      if (waitTimer.current) clearTimeout(waitTimer.current);
      if (testingRef.current) {
        pubMove('stop');
        setTimeout(() => pubMove('stop'), 150);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 보드가 보내는 oc 응답
  useEffect(() => {
    if (!mqttMessage) return;
    try {
      const m = JSON.parse(mqttMessage);
      if (m.mac !== mac || m.command !== 'ottoCal') return;
      if (waitTimer.current) { clearTimeout(waitTimer.current); waitTimer.current = null; }
      setWaiting('');
      if (m.ok === 0 || m.ok === false) {
        setError(m.err === 'busy' ? 'AI 대화 중에는 보정할 수 없습니다. 먼저 AI 대화를 끄세요.'
          : m.err === 'nvs_fail' ? '보드에 저장하지 못했습니다.'
          : `보정 오류 (${m.err || '알 수 없음'})`);
        setMsg('');
        return;
      }
      setError('');
      setLoaded(true);
      if (typeof m.calSaved === 'number') setSaved(m.calSaved === 1);
      // 사용자가 방금 만졌으면 보드가 되돌려 준 옛 값으로 덮어쓰지 않는다 (조회/저장/기본값 응답은 항상 반영)
      const force = m.action === 'get' || m.action === 'save' || m.action === 'clear' || m.action === 'restore';
      if (force || Date.now() - touchedAt.current > 800) {
        setV((prev) => ({
          yl: typeof m.calYl === 'number' ? m.calYl : prev.yl,
          yr: typeof m.calYr === 'number' ? m.calYr : prev.yr,
          tr: typeof m.calTiltR === 'number' ? m.calTiltR : prev.tr,
          tl: typeof m.calTiltL === 'number' ? m.calTiltL : prev.tl,
          rl: typeof m.calSpeedL === 'number' ? m.calSpeedL : prev.rl,
          rr: typeof m.calSpeedR === 'number' ? m.calSpeedR : prev.rr,
          dRYr: typeof m.calDanceRYr === 'number' ? m.calDanceRYr : prev.dRYr,
          dRYl: typeof m.calDanceRYl === 'number' ? m.calDanceRYl : prev.dRYl,
          dLYr: typeof m.calDanceLYr === 'number' ? m.calDanceLYr : prev.dLYr,
          dLYl: typeof m.calDanceLYl === 'number' ? m.calDanceLYl : prev.dLYl,
        }));
      }
      if (m.action === 'save') setMsg('보드에 저장했습니다. 다음 부팅부터 이 값을 씁니다.');
      else if (m.action === 'clear') setMsg('저장된 값을 지우고 기본값으로 되돌렸습니다.');
      else if (m.action === 'p0') setMsg('조립 자세입니다. 다리를 끼우고 나사를 조이세요.');
      else if (m.action === 'p90') setMsg('다리를 90도로 보냈습니다.');
    } catch (e) {
      // 다른 메시지는 무시한다
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mqttMessage]);

  const keyOf: Record<keyof CalValues, string> = { yl: 'calYl', yr: 'calYr', tr: 'calTiltR', tl: 'calTiltL', rl: 'calSpeedL', rr: 'calSpeedR', dRYr: 'calDanceRYr', dRYl: 'calDanceRYl', dLYr: 'calDanceLYr', dLYl: 'calDanceLYl' };

  // 값을 바꾸면 화면에 바로 보이고, 보드에는 0.15초에 한 번만 보낸다 (마지막 값은 반드시 보낸다)
  const flush = useCallback(() => {
    const p = pending.current;
    pending.current = {};
    timer.current = null;
    const extra: Record<string, number> = {};
    (Object.keys(p) as (keyof CalValues)[]).forEach((k) => { extra[keyOf[k]] = p[k] as number; });
    if (Object.keys(extra).length) {
      sentAt.current = Date.now();
      pub('set', extra);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pub]);

  const change = useCallback((key: keyof CalValues, value: number, lo: number, hi: number) => {
    const next = Math.max(lo, Math.min(hi, Math.round(value)));
    touchedAt.current = Date.now();
    setMsg('');
    setSaved((s) => (s === null ? s : false)); // 바꾸면 "저장 안 됨" 표시로
    setV((prev) => ({ ...prev, [key]: next }));
    pending.current[key] = next;
    const wait = 150 - (Date.now() - sentAt.current);
    if (wait <= 0) {
      if (timer.current) clearTimeout(timer.current);
      flush();
    } else if (!timer.current) {
      timer.current = setTimeout(flush, wait);
    }
  }, [flush]);

  const startTest = (action: string) => {
    flash(action);
    setError('');
    testingRef.current = true;
    setTesting(action);
    pubMove(action);
  };
  const stopTest = () => {
    if (testingRef.current) flash('stop');
    testingRef.current = false;
    setTesting(null);
    pubMove('stop');
    setTimeout(() => pubMove('stop'), 150);
  };

  const save = () => { stopTest(); send('save', '보드에 저장', 'save'); };
  const reset = () => {
    if (!window.confirm('저장된 보정값을 지우고 기본값으로 되돌릴까요?')) return;
    stopTest();
    send('clear', '기본값으로 되돌리기', 'clear');
  };
  const copyValues = async () => {
    const text = `YL=${v.yl}, YR=${v.yr}, 오른발기울기=${v.tr}, 왼발기울기=${v.tl}, 왼발속도=${v.rl}, 오른발속도=${v.rr}, 댄스(오른발서기 YR/YL)=${v.dRYr}/${v.dRYl}, 댄스(왼발서기 YR/YL)=${v.dLYr}/${v.dLYl}`;
    try {
      await navigator.clipboard.writeText(text);
      setMsg('값을 복사했습니다. 펌웨어를 다시 설치하면 보정값이 지워지니 기록해 두세요.');
    } catch (e) {
      setMsg(text);
    }
  };

  // 슬라이더 + 미세 조정 버튼 한 줄
  const row = (label: string, key: keyof CalValues, range: readonly [number, number], unit: string, steps: number[], labelWidth = 130) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
      <span style={{ minWidth: `${labelWidth}px` }}>{label}</span>
      {steps.slice().reverse().map((s) => (
        <button key={`-${s}`} type="button" style={smallBtn} onClick={() => change(key, v[key] - s, range[0], range[1])}>-{s}</button>
      ))}
      <input
        type="range" min={range[0]} max={range[1]} step={1} value={v[key]}
        onChange={(e) => change(key, Number(e.target.value), range[0], range[1])}
        style={{ flex: '1 1 160px', minWidth: '120px' }}
      />
      {steps.map((s) => (
        <button key={`+${s}`} type="button" style={smallBtn} onClick={() => change(key, v[key] + s, range[0], range[1])}>+{s}</button>
      ))}
      <b style={{ minWidth: '70px', textAlign: 'right' }}>{v[key]}{unit}</b>
    </div>
  );

  return (
    <div style={panelStyle}>
      <style>{`
        @keyframes calShake { 0%{transform:translateX(0)} 20%{transform:translateX(-5px)} 40%{transform:translateX(5px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} 100%{transform:translateX(0)} }
        .cal-shake { animation: calShake 0.45s; box-shadow: 0 0 0 3px rgba(37,99,235,0.45); }
        .cal-btn:active { transform: scale(0.94); filter: brightness(0.88); }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: 'bold', fontSize: '1.05rem' }}>로봇 맞추기</div>
        <button type="button" style={closeBtnStyle} onClick={() => { stopTest(); onClose(); }}>닫기</button>
      </div>
      <div style={{ ...hintStyle, marginTop: '4px' }}>
        {loaded ? '' : '보드의 현재 값을 읽는 중... (응답이 없으면 보드가 켜져 있고 연결됐는지 확인하세요)'}
        {loaded && (saved === null ? '' : saved ? '✅ 보드에 저장된 값이 있습니다.' : '⚠ 아직 저장하지 않은 값입니다. 맞춘 뒤 [저장]을 누르세요.')}
      </div>

      <>
          <div style={stepStyle}>1. 조립 자세 (다리 0도 / 180도)</div>
          <div style={hintStyle}>이 자세로 다리를 끼우고 나사를 조이세요. (오른쪽 다리 0도, 왼쪽 다리 180도)</div>
          <button type="button" className={`action-btn cal-btn${pressed === 'p0' ? ' cal-shake' : ''}`} style={moveBtnStyle} onClick={() => { stopTest(); send('p0', '모터를 0도 / 180도로 움직이기', 'p0'); }}>모터를 0도 / 180도로 움직이기</button>

          <div style={stepStyle}>2. 다리를 수직(90도)으로 맞추기</div>
          <div style={hintStyle}>[모터를 90도로 움직이기]를 누른 뒤, 두 다리가 정확히 수직이 되도록 숫자를 조금씩 바꾸세요. 바꾸는 즉시 다리가 움직입니다.</div>
          <button type="button" className={`action-btn cal-btn${pressed === 'p90' ? ' cal-shake' : ''}`} style={{ ...moveBtnStyle, marginBottom: '8px' }} onClick={() => { stopTest(); send('p90', '모터를 90도로 움직이기', 'p90'); }}>모터를 90도로 움직이기</button>
          {row('왼쪽 다리 (YL)', 'yl', RANGE.center, ' us', [1, 10])}
          {row('오른쪽 다리 (YR)', 'yr', RANGE.center, ' us', [1, 10])}

          <div style={stepStyle}>3. 굴리기 시험 (두 발 속도)</div>
          <div style={hintStyle}>앞으로 굴리면서 로봇이 똑바로 가도록 두 발의 속도를 맞추세요.</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button type="button" className={`action-btn cal-btn${pressed === 'rf' ? ' cal-shake' : ''}`} style={testing === 'rf' ? startBtnOnStyle : startBtnStyle} onClick={() => startTest('rf')}>앞으로 굴리기</button>
            <button type="button" className={`action-btn cal-btn${pressed === 'stop' ? ' cal-shake' : ''}`} style={stopBtnStyle} onClick={stopTest}>정지</button>
          </div>
          {row('왼발 속도 (RL)', 'rl', RANGE.speed, ' us', [1, 10])}
          {row('오른발 속도 (RR)', 'rr', RANGE.speed, ' us', [1, 10])}

          <div style={stepStyle}>4. 걷기 시험 (발을 들 때 기울기)</div>
          <div style={hintStyle}>
            [걷기 시작]으로 앞으로 걷게 하고, 걸을 때 발이 수평으로 들리도록 기울기를 조금씩 바꾸세요.
            로봇이 안 들리면 올리고, 한쪽으로 돌면 그쪽을 더 올립니다. (먼저 2번에서 다리 수직을 맞추세요)
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button type="button" className={`action-btn cal-btn${pressed === 'wf' ? ' cal-shake' : ''}`} style={testing === 'wf' ? startBtnOnStyle : startBtnStyle} onClick={() => startTest('wf')}>걷기 시작</button>
            <button type="button" className={`action-btn cal-btn${pressed === 'stop' ? ' cal-shake' : ''}`} style={stopBtnStyle} onClick={stopTest}>정지</button>
          </div>
          {row('오른발을 들 때', 'tr', RANGE.tilt, ' 도', [1, 5])}
          {row('왼발을 들 때', 'tl', RANGE.tilt, ' 도', [1, 5])}

          <div style={stepStyle}>5. 댄스 시험 (한 발로 설 때 엉덩이 각도)</div>
          <div style={hintStyle}>
            [댄스 시작]으로 춤을 추게 하고, 한 발로 설 때 반대쪽 발이 잘 들리면서 넘어지지 않도록 각도를 조금씩 바꾸세요.
            발이 덜 들리면 90에서 더 멀게 하고, 넘어지면 줄입니다. 왼발 서기는 90보다 크게(90~150), 오른발 서기는 90보다 작게(30~90) 맞춥니다. 서보 보호를 위해 오른쪽 엉덩이(YR)는 135 이하, 왼쪽 엉덩이(YL)는 55 이상으로 자동 제한됩니다.
            (넘어지기 쉬우니 손으로 받치거나 푹신한 바닥에서 시험하세요.)
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button type="button" className={`action-btn cal-btn${pressed === 'dn' ? ' cal-shake' : ''}`} style={testing === 'dn' ? startBtnOnStyle : startBtnStyle} onClick={() => startTest('dn')}>댄스 시작</button>
            <button type="button" className={`action-btn cal-btn${pressed === 'stop' ? ' cal-shake' : ''}`} style={stopBtnStyle} onClick={stopTest}>정지</button>
          </div>
          {row('왼발 서기: 오른쪽 엉덩이 (YR)', 'dRYr', RANGE.danceRYr, ' 도', [1, 5], 230)}
          {row('왼발 서기: 왼쪽 엉덩이 (YL)', 'dRYl', RANGE.danceRYl, ' 도', [1, 5], 230)}
          {row('오른발 서기: 오른쪽 엉덩이 (YR)', 'dLYr', RANGE.danceLYr, ' 도', [1, 5], 230)}
          {row('오른발 서기: 왼쪽 엉덩이 (YL)', 'dLYl', RANGE.danceLYl, ' 도', [1, 5], 230)}
      </>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
        <button type="button" className={`action-btn btn-primary cal-btn${pressed === 'save' ? ' cal-shake' : ''}`} onClick={save}>저장 (보드 NVS)</button>
        <button type="button" className="action-btn cal-btn" style={grayBtnStyle} onClick={copyValues}>값 복사</button>
        <button type="button" className="action-btn" style={{ backgroundColor: '#ef4444', color: '#ffffff' }} onClick={reset}>기본값으로</button>
      </div>

      <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#475569' }}>
        현재 값: YL {v.yl} / YR {v.yr} / 기울기 {v.tr}, {v.tl} / 속도 {v.rl}, {v.rr} / 댄스 {v.dRYr}, {v.dRYl}, {v.dLYr}, {v.dLYl}
      </div>
      {waiting && (
        <div style={{ marginTop: '8px', color: '#b45309', fontSize: '0.9rem', fontWeight: 'bold' }}>
          ⏳ 보드로 보냈습니다: {waiting} … 응답을 기다리는 중
        </div>
      )}
      {msg && <div style={{ marginTop: '8px', color: '#2563eb', fontSize: '0.9rem' }}>✅ {msg}</div>}
      {error && (
        <div style={{ marginTop: '8px', padding: '10px 12px', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', color: '#b91c1c', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#94a3b8' }}>
        참고: 웹에서 펌웨어를 다시 설치하면 보드의 저장값이 지워지니 [값 복사]로 기록해 두세요.
      </div>
    </div>
  );
};

export default OttoCalibration;
